import {useState, useEffect, Dispatch, SetStateAction, CSSProperties, useMemo} from "react";
import CreateAnotherCheckBox from "@/components/CreateAnotherCheckBox";
import {createInitialData, DataSource, useDataSource} from "./DataSource";
import MultipleLineError from "@/components/MultipleLineError";
import {CapabilitySetsWidget} from "../limit-widgets/CapabilitySetsWidget";
import {CreateExtensionForm} from "./CreateExtensionForm";
import {CircularProgress, Dialog, MenuItem, Select, TextField} from "@mui/material";
import {FormattedFormRow, FormContainer, FormDialogActionButtons, FormRowDivider} from "@dvrt/graphql-widgets";
import {Link} from "@/components/widgets";
import {DialogActions, DialogContent, FormattedDialogTitle} from "@dvrt/graphql-widgets";
import {useIntl, defineMessage} from "react-intl";
import {showNotification} from "@/actions";
import {useFormGuard} from "@/utils/formGuard";
import {Limit, LIMIT_ERROR_MESSAGES, LimitError} from "../types";
import ErrorMessageDivider from "@/components/ErrorMessageDivider";
import {getRandomString, insertSeparator} from "@/tools";
import QuotaItemSelector from "@/components/QuotaItemSelector";
import {userPackageAllocationToQuotaItem} from "../limit-widgets/common";
import {CreateUserData, CreateUserLoginData} from "@/api";
import DeviceTypeSetAllocator from "../limit-widgets/DeviceTypeSetAllocator";
import {useAuth} from "@/context/AuthProvider";
import {commonTranslations} from "@/utils/translations";
import LanguageSelector from "@/components/LanguageSelector";

type ContentProps = {
    onSuccess: () => void;
    close: () => void;
    dataSource: DataSource;
    domainId: number;
};

type CreateExtensionDialogProps = {
    domainId: number;
    isOpen: boolean;
    onSuccess: () => void;
    close: () => void;
};

const successNotification = (form: CreateExtensionForm) => {
    const url = `/domains/${form.domainId}/users/${form.result.data.createUser.id}/`;
    return showNotification(
        defineMessage({id: "extension_created", defaultMessage: "User {link} has been successfully created."}),
        "success",
        {
            link: <Link to={url}>{form.values.shortNumber ?? form.values.e164}</Link>,
        },
        10000,
    );
};

const getMaxForLimit = (limit: Limit, canAutoincrement: boolean) => {
    let max: number;

    if (canAutoincrement) {
        max = limit.system.limit;
    } else {
        max = limit.domain.allocated;
    }

    return max - limit.domain.used;
};

const capSetCanBeChecked = (set: Limit, canAutoincrement: boolean) => {
    const max = getMaxForLimit(set, canAutoincrement);
    return max === undefined || max > 0;
};

// TODO: Rather the Form should manage the DataSource. (Creating it, etc...)
const Content = ({dataSource, domainId, onSuccess, close}: ContentProps) => {
    const auth = useAuth();
    const canAutoincrement = auth.hasPermission("auto_increment_domain_quota");

    const [createAnother, setCreateAnother] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const setsStyle: CSSProperties = {};

    // pin should not change with every render, so we use useMemo
    const initialData = useMemo(() => {
        const copiedInitialData = createInitialData(
            dataSource.defaultDeviceLimits,
            dataSource.locations[0].id,
            dataSource.defaultUserPackage,
            dataSource.defaultUserCapabilitySets,
            dataSource.defaultLanguage,
        );
        const packages = dataSource.userPackages;

        if (!copiedInitialData.userPackageName && packages?.length > 0) {
            copiedInitialData.userPackageName = packages[0].name;
        }

        return {
            ...copiedInitialData,
            pin: getRandomString(4, "0123456789"),
        } as CreateUserData;
    }, [dataSource]);

    const form = new CreateExtensionForm(dataSource, initialData, domainId, successNotification);
    useFormGuard(form.hasChanged);

    // Validate each time the user changes a value
    useEffect(() => {
        form.validate();
        // form.values is using useState, so it is safe to use it here
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.values]);

    function handleClose() {
        close();
        form.reset();
    }

    function reset() {
        // the initial data generator is used to get the same initial
        // data but with a different pin every time
        form.newInitialData(initialData);
    }

    async function submit() {
        setSubmitting(true);
        const res = await form.submit();
        setSubmitting(false);
        if (!res) return;

        onSuccess();

        if (createAnother) reset();
        else handleClose();
    }

    const updateUser = (data: Partial<CreateUserLoginData>) =>
        form.updateValues({login: {...form.values.login, ...data}});

    const dividerProps = {
        verticalPadding: 6,
        horizontalPadding: 2,
    };

    const userLimits = useMemo(() => {
        const result: Record<string, {form: number; current?: number}> = {};

        for (const set in dataSource.deviceTypeSetsInfo.domain) {
            const value = form.values.deviceTypeSets.find((s) => s.name === set)?.value ?? 0;
            result[set] = {
                form: value,
            };
        }

        console.log("User limits for device type sets", result);

        return result;
    }, [dataSource.deviceTypeSetsInfo.domain, form.values.deviceTypeSets]);

    return (
        <>
            <FormattedDialogTitle title={defineMessage({id: "new_extension", defaultMessage: "New user"})} />
            <DialogContent>
                <div style={{display: "flex"}}>
                    <div style={{display: "block"}}>
                        <FormContainer>
                            <FormattedFormRow label={defineMessage({id: "e164", defaultMessage: "E.164"})}>
                                <TextField
                                    variant="standard"
                                    disabled={submitting}
                                    required={dataSource.isResidential}
                                    margin="dense"
                                    fullWidth
                                    value={form.values.e164}
                                    onChange={(event) => form.updateValues({e164: event.target.value})}
                                    error={form.errorHelper.hasError("e164")}
                                    helperText={<MultipleLineError errors={form.errorHelper.fieldErrors("e164")} />}
                                />
                            </FormattedFormRow>
                            {!dataSource.isResidential && (
                                <FormattedFormRow
                                    label={defineMessage({id: "identifier", defaultMessage: "Identifier"})}
                                    required
                                >
                                    <TextField
                                        variant="standard"
                                        disabled={submitting}
                                        required
                                        fullWidth
                                        margin="dense"
                                        value={form.values.shortNumber}
                                        onChange={(event) => form.updateValues({shortNumber: event.target.value})}
                                        error={form.errorHelper.hasError("shortNumber")}
                                        helperText={
                                            <MultipleLineError errors={form.errorHelper.fieldErrors("shortNumber")} />
                                        }
                                    />
                                </FormattedFormRow>
                            )}
                            <FormattedFormRow
                                label={defineMessage({id: "displayName", defaultMessage: "Name"})}
                                required
                            >
                                <TextField
                                    variant="standard"
                                    disabled={submitting}
                                    required
                                    fullWidth
                                    margin="dense"
                                    value={form.values.displayName}
                                    onChange={(event) => form.updateValues({displayName: event.target.value})}
                                    error={form.errorHelper.hasError("displayName")}
                                    helperText={
                                        <MultipleLineError errors={form.errorHelper.fieldErrors("displayName")} />
                                    }
                                />
                            </FormattedFormRow>
                            <FormattedFormRow label={defineMessage({id: "pin", defaultMessage: "Pin code"})} required>
                                <TextField
                                    variant="standard"
                                    disabled={submitting}
                                    required
                                    fullWidth
                                    margin="dense"
                                    value={form.values.pin}
                                    onChange={(event) => form.updateValues({pin: event.target.value})}
                                    error={form.errorHelper.hasError("pin")}
                                    helperText={<MultipleLineError errors={form.errorHelper.fieldErrors("pin")} />}
                                />
                            </FormattedFormRow>
                            <FormattedFormRow label={defineMessage({id: "location.label", defaultMessage: "Location"})}>
                                <Select
                                    variant="standard"
                                    style={{marginTop: 9}}
                                    disabled={submitting}
                                    fullWidth
                                    margin="dense"
                                    value={form.values.locationId}
                                    onChange={(event) => form.updateValues({locationId: Number(event.target.value)})}
                                >
                                    {dataSource.locations.map((loc) => (
                                        <MenuItem key={loc.id} value={loc.id}>
                                            {loc.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormattedFormRow>
                            <FormRowDivider {...dividerProps} />
                            {dataSource.userPackages.length > 0 && (
                                <FormattedFormRow
                                    label={defineMessage({id: "user_package", defaultMessage: "User package"})}
                                >
                                    <QuotaItemSelector
                                        style={{marginTop: 9}}
                                        initialValue={dataSource.userPackages[0].name}
                                        value={form.values.userPackageName}
                                        updateValue={(val) => form.updateValues({userPackageName: val})}
                                        disabled={submitting}
                                        items={dataSource.userPackageAllocationInfo.map(
                                            userPackageAllocationToQuotaItem,
                                        )}
                                        itemNamePrefix="user_package_names."
                                    />
                                </FormattedFormRow>
                            )}
                            {dataSource.domainQuotas.capabilitySets.length > 0 && (
                                <FormattedFormRow
                                    label={defineMessage({
                                        id: "extensionLimits.capabilities",
                                        defaultMessage: "Capabilities",
                                    })}
                                    inputStyle={setsStyle}
                                    labelStyle={setsStyle}
                                >
                                    <CapabilitySetsWidget
                                        disabled={submitting}
                                        setCanBeChecked={(set) => capSetCanBeChecked(set, canAutoincrement)}
                                        value={form.values.capabilitySets}
                                        updateValue={(v) => form.updateValues({capabilitySets: v})}
                                        sets={form.capabilityLimits}
                                        containerStyle={{marginTop: 4}}
                                        upCaps={dataSource.getPackageCapabilitySets(form.values.userPackageName)}
                                    />
                                </FormattedFormRow>
                            )}{" "}
                            {dataSource.deviceTypeSets.length && (
                                <FormattedFormRow
                                    label={defineMessage({id: "extensionLimits.devices", defaultMessage: "Devices"})}
                                    inputStyle={setsStyle}
                                    labelStyle={setsStyle}
                                >
                                    <DeviceTypeSetAllocator
                                        userPackageDeviceTypeSets={{
                                            form: dataSource.getPackageDeviceTypeSets(form.values.userPackageName),
                                        }}
                                        userLimits={userLimits}
                                        updateValue={(name, value) => form.setDeviceTypeSetQuota(name, value)}
                                        systemLimits={dataSource.deviceTypeSetsInfo.systemLimit}
                                        domainLimits={dataSource.deviceTypeSetsInfo.domain}
                                        deviceTypeSets={dataSource.deviceTypeSets}
                                        disabled={submitting}
                                    />
                                </FormattedFormRow>
                            )}
                            <FormRowDivider {...dividerProps} />
                            <FormattedFormRow label={defineMessage({id: "email", defaultMessage: "Email"})}>
                                <TextField
                                    variant="standard"
                                    disabled={submitting}
                                    margin="dense"
                                    fullWidth
                                    value={form.values.login.email}
                                    onChange={(event) => updateUser({email: event.target.value})}
                                    error={form.errorHelper.hasError("user.email")}
                                    helperText={
                                        <MultipleLineError errors={form.errorHelper.fieldErrors("user.email")} />
                                    }
                                    autoComplete="chrome-off"
                                />
                            </FormattedFormRow>
                            <FormattedFormRow label={commonTranslations.language}>
                                <LanguageSelector
                                    variant="standard"
                                    disabled={submitting}
                                    style={{marginTop: 6}}
                                    value={form.values.login.language}
                                    onChange={(language) => updateUser({language})}
                                    languages={dataSource.availableTranslations}
                                />
                            </FormattedFormRow>
                        </FormContainer>
                    </div>
                </div>
            </DialogContent>
            <ActionButtonContainer
                formObj={{form: form}}
                submit={submit}
                createAnother={createAnother}
                setCreateAnother={setCreateAnother}
                handleClose={handleClose}
                submitting={submitting}
            />
        </>
    );
};

type ActionButtonContainerProps = {
    // Using an object to get the form, otherwise it does not work
    formObj: {form: CreateExtensionForm};
    // Parent variables and functions
    submit: () => void;
    createAnother: boolean;
    setCreateAnother: Dispatch<SetStateAction<boolean>>;
    handleClose: () => void;
    submitting: boolean;
    style?: CSSProperties;
};

const ActionButtonContainer = ({
    formObj,
    submit,
    createAnother,
    setCreateAnother,
    handleClose,
    submitting,
    style,
}: ActionButtonContainerProps) => {
    const [errors, setErrors] = useState<LimitError[]>([]);
    const {form} = formObj;
    const intl = useIntl();

    // Whenever there is a change in form errors, update the errors state to properly display them
    useEffect(() => {
        setErrors(form.validationErrors);
    }, [form.validationErrors]);

    // Translate and map the errors to a list of messages with dividers between them
    function formatErrors() {
        if (errors.length === 0) return null;

        const errorMessages = errors.map((e) => intl.formatMessage(LIMIT_ERROR_MESSAGES[e.type], {name: e.name}));

        return (
            <div>
                {insertSeparator(
                    errorMessages.map((m, index) => (
                        <span key={index} style={{display: "block"}}>
                            {m}
                        </span>
                    )),
                    <ErrorMessageDivider />,
                )}
            </div>
        );
    }

    return (
        <DialogActions style={{paddingLeft: 60, ...style}}>
            <CreateAnotherCheckBox state={createAnother} setState={setCreateAnother} />
            <FormDialogActionButtons
                mainAction={submit}
                secondaryAction={() => form.reset()}
                mainButtonEnabled={form.hasChanged && form.valid}
                secondaryButtonEnabled={form.hasChanged}
                errorMarker={!form.valid}
                errorMarkerTooltipTitle={formatErrors()}
                cancel={handleClose}
                pending={submitting}
            />
        </DialogActions>
    );
};

const CreateExtensionDialog = ({close, domainId, isOpen, onSuccess}: CreateExtensionDialogProps) => {
    const dataSource = useDataSource(domainId, !isOpen);

    // this is useful when e.g. locations are changed by someone else in the background
    // so it fetches the data again when the dialog is opened
    useEffect(() => {
        if (isOpen) dataSource.refetch();
    }, [dataSource, isOpen]);

    if (!isOpen) return null;

    return (
        <Dialog open={isOpen} maxWidth="xl">
            {dataSource.loading ? (
                <CircularProgress sx={{margin: "inherit"}} />
            ) : (
                <Content dataSource={dataSource} onSuccess={onSuccess} close={close} domainId={domainId} />
            )}
        </Dialog>
    );
};
export default CreateExtensionDialog;
